/*
 * ============================================================
 * MODI — global runtime (dependency-free, classic script)
 * Currently: the scroll-reveal system. Heavier scroll/media
 * logic (salon sequence) lives in its own section asset.
 *
 * Content is never hidden unless `.modi-motion` is on <html>
 * (added pre-paint by an inline script), so no-JS / reduced
 * motion users always see everything.
 *
 * Reveal detection uses getBoundingClientRect, NOT
 * IntersectionObserver, on purpose:
 *   1. The `--clip` reveal variant clips its element to zero
 *      height (clip-path: inset(0 0 100%)). Chrome factors
 *      clip-path into IntersectionObserver, so a clipped element
 *      reports a 0 intersection ratio and would DEADLOCK — it
 *      could never reveal itself. getBoundingClientRect returns
 *      the true (unclipped) border box, so it is immune.
 *   2. Horizon scrolls an inner `.page-wrapper`, not the window,
 *      so we listen for scroll in the CAPTURE phase to catch
 *      scrolling from any container.
 * The sweep is rAF-throttled and unbinds once everything is
 * revealed, so the steady-state cost is zero.
 * ============================================================
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var pending = [];
  var ticking = false;
  var bound = false;

  function inView(el) {
    var r = el.getBoundingClientRect(); // immune to clip-path / opacity
    if (r.width === 0 && r.height === 0) return false; // display:none
    var vh = window.innerHeight || document.documentElement.clientHeight;
    // Reveal once the element's top has risen into the lower ~92% of the
    // viewport (mirrors the previous rootMargin: '0 0 -8% 0').
    return r.top < vh * 0.92 && r.bottom > 0;
  }

  function sweep() {
    ticking = false;
    for (var i = pending.length - 1; i >= 0; i--) {
      if (inView(pending[i])) {
        pending[i].classList.add('is-visible');
        pending.splice(i, 1);
      }
    }
    if (!pending.length) unbind();
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(sweep);
  }

  function bind() {
    if (bound) return;
    bound = true;
    // Capture phase: catches window scroll AND inner overflow-container
    // scroll (Horizon scrolls a `.page-wrapper`, so a plain window
    // 'scroll' listener never fires).
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
  }

  function unbind() {
    if (!bound) return;
    bound = false;
    window.removeEventListener('scroll', schedule, { capture: true });
    window.removeEventListener('resize', schedule);
  }

  /**
   * Register reveal elements within `root`. Safe to call repeatedly
   * (Shopify theme-editor section reloads, dynamically injected content).
   */
  function initReveals(root) {
    var scope = root || document;
    var els = Array.prototype.slice.call(
      scope.querySelectorAll('.modi-reveal:not(.is-visible)')
    );
    if (!els.length) return;

    // No motion preference: show immediately, never hold content back.
    if (prefersReduced.matches) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    els.forEach(function (el) {
      if (pending.indexOf(el) === -1) pending.push(el);
    });
    bind();
    schedule(); // initial sweep reveals anything already in view
  }

  /**
   * Editorial rails: [data-modi-rail-step="prev|next"] buttons scroll the
   * sibling [data-modi-rail] by one card. Document-level delegation so
   * theme-editor section re-renders never lose the behaviour; clicks at
   * either end are clamped no-ops by scrollBy itself.
   */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-modi-rail-step]') : null;
    if (!btn) return;
    var scope = btn.closest('[data-modi-section]') || document;
    var rail = scope.querySelector('[data-modi-rail]');
    if (!rail) return;
    var card = rail.firstElementChild;
    var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    var step = card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
    var dir = btn.getAttribute('data-modi-rail-step') === 'prev' ? -1 : 1;
    rail.scrollBy({ left: step * dir, behavior: prefersReduced.matches ? 'auto' : 'smooth' });
  });

  // The zoomed product gallery is laid out as a horizontal snap slider (see
  // modi-foundation.css). Horizon's zoom dialog only binds Escape, so arrow
  // keys are added here: a slider you cannot page with the keyboard is not one.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    var dialog = document.querySelector('zoom-dialog dialog[open]');
    if (!dialog) return;
    // Let the browser handle it while a control inside the dialog has focus.
    var active = document.activeElement;
    if (active && active !== dialog && active.closest('.dialog-thumbnails-list')) return;
    event.preventDefault();
    dialog.scrollBy({
      left: dialog.clientWidth * (event.key === 'ArrowRight' ? 1 : -1),
      behavior: prefersReduced.matches ? 'auto' : 'smooth',
    });
  });

  function boot() {
    initReveals(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Shopify theme editor: re-scan reveals when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (event) {
    initReveals(event.target);
  });

  // Expose for section scripts that inject content dynamically.
  window.MODI = window.MODI || {};
  window.MODI.initReveals = initReveals;
})();
